import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// STL 파일을 3D로 렌더링
// props:
//   url: STL 파일 URL (auth 토큰 포함)
//   className: 래퍼 클래스
//   height: 높이 (기본 320)
export default function StlViewer({ url, className = '', height = 320 }) {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null); // { sizeX, sizeY, sizeZ, triangles }

  useEffect(() => {
    if (!url || !mountRef.current) return;

    const mount = mountRef.current;
    const width = mount.clientWidth;
    const h = height;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / h, 0.1, 5000);
    camera.position.set(150, 150, 200);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    mount.appendChild(renderer.domElement);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(100, 200, 100);
    scene.add(dirLight);
    const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
    backLight.position.set(-100, -100, -100);
    scene.add(backLight);

    // Grid floor
    const grid = new THREE.GridHelper(300, 30, 0x333333, 0x1f1f1f);
    scene.add(grid);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;

    // STL 로딩
    const loader = new STLLoader();
    let mesh = null;
    let cancelled = false;

    // fetch로 먼저 Blob으로 받아 auth 헤더 사용
    const token = localStorage.getItem('mfg_token');
    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(buffer => {
        if (cancelled) return;
        const geometry = loader.parse(buffer);
        geometry.center();
        geometry.computeBoundingBox();

        const bbox = geometry.boundingBox;
        const size = new THREE.Vector3();
        bbox.getSize(size);
        setMeta({
          sizeX: size.x.toFixed(1),
          sizeY: size.y.toFixed(1),
          sizeZ: size.z.toFixed(1),
          triangles: geometry.attributes.position.count / 3,
        });

        const material = new THREE.MeshPhongMaterial({
          color: 0xf4b8b8,
          specular: 0x222222,
          shininess: 30,
          flatShading: false,
        });
        mesh = new THREE.Mesh(geometry, material);
        // STL은 보통 Z-up, three는 Y-up이므로 회전
        mesh.rotation.x = -Math.PI / 2;
        scene.add(mesh);

        // 카메라를 모델 크기에 맞춤
        const maxDim = Math.max(size.x, size.y, size.z);
        const dist = maxDim * 1.8;
        camera.position.set(dist, dist * 0.8, dist);
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();

        setLoading(false);
      })
      .catch(e => {
        if (!cancelled) {
          console.error('[StlViewer] 로딩 실패:', e);
          setError(e.message);
          setLoading(false);
        }
      });

    // Animate
    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const handleResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      renderer.dispose();
      if (mount && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [url, height]);

  return (
    <div className={`relative bg-gray-950 rounded-lg overflow-hidden ${className}`}>
      <div ref={mountRef} style={{ height: `${height}px`, width: '100%' }} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-950/80">
          <p className="text-gray-500 text-sm">3D 모델 로딩 중...</p>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-950/80">
          <p className="text-red-400 text-sm">로딩 실패: {error}</p>
        </div>
      )}
      {meta && !loading && (
        <div className="absolute top-2 left-2 bg-gray-900/80 backdrop-blur border border-gray-800 rounded-lg px-2 py-1">
          <p className="text-gray-400 text-[10px] font-mono">
            {meta.sizeX} × {meta.sizeY} × {meta.sizeZ} mm · {meta.triangles.toLocaleString()}면
          </p>
        </div>
      )}
      {!loading && !error && (
        <div className="absolute bottom-2 right-2 bg-gray-900/80 backdrop-blur border border-gray-800 rounded-lg px-2 py-1">
          <p className="text-gray-500 text-[10px]">드래그 회전 · 휠 확대</p>
        </div>
      )}
    </div>
  );
}
